import { __extends } from "tslib";
import { MDCNotchedOutline } from '@material/notched-outline';
import { BaseAdapter } from '../core/adapters/base-adapter';
import { getShadowElement, Platform, calculateFontWidth } from '@tyler-components-web/core';
import { CHIP_FIELD_CONSTANTS } from './chip-field-constants';
import { FloatingLabel } from '../floating-label/floating-label';
import { getActiveElement } from 'shadow-utils';
var ChipFieldAdapter = /** @class */ (function (_super) {
    __extends(ChipFieldAdapter, _super);
    function ChipFieldAdapter(component) {
        var _this = _super.call(this, component) || this;
        _this._rootElement = getShadowElement(component, CHIP_FIELD_CONSTANTS.selectors.ROOT);
        _this._notchedOutlineElement = getShadowElement(_this._component, CHIP_FIELD_CONSTANTS.selectors.NOTCHED_OUTLINE);
        _this._labelSlot = getShadowElement(_this._component, CHIP_FIELD_CONSTANTS.selectors.LABEL_SLOT);
        _this._memberSlot = getShadowElement(_this._component, CHIP_FIELD_CONSTANTS.selectors.MEMBER_SLOT);
        return _this;
    }
    ChipFieldAdapter.prototype.addRootClass = function (name) {
        this._rootElement.classList.add(name);
    };
    ChipFieldAdapter.prototype.removeRootClass = function (name) {
        this._rootElement.classList.remove(name);
    };
    ChipFieldAdapter.prototype.addRootEventListener = function (type, callback) {
        this._rootElement.addEventListener(type, callback);
    };
    ChipFieldAdapter.prototype.removeRootEventListener = function (type, callback) {
        this._rootElement.removeEventListener(type, callback);
    };
    ChipFieldAdapter.prototype.addLabelSlotListener = function (listener) {
        this._labelSlot.addEventListener('slotchange', listener);
    };
    ChipFieldAdapter.prototype.removeLabelSlotListener = function (listener) {
        if (this._labelSlot) {
            this._labelSlot.removeEventListener('slotchange', listener);
        }
    };
    ChipFieldAdapter.prototype.addMemberSlotListener = function (listener) {
        this._memberSlot.addEventListener('slotchange', listener);
    };
    ChipFieldAdapter.prototype.removeMemberSlotListener = function (listener) {
        if (this._memberSlot) {
            this._memberSlot.removeEventListener('slotchange', listener);
        }
    };
    ChipFieldAdapter.prototype.addLabelClass = function (name) {
        if (!this.labelElement) {
            return;
        }
        this.labelElement.classList.add(name);
    };
    ChipFieldAdapter.prototype.removeLabelClass = function (name) {
        if (!this.labelElement) {
            return;
        }
        this.labelElement.classList.remove(name);
    };
    ChipFieldAdapter.prototype.setInputClass = function (className) {
        if (!this.inputElement) {
            return;
        }
        this.inputElement.classList.add(className);
    };
    ChipFieldAdapter.prototype.removeInputClass = function (className) {
        if (!this.inputElement) {
            return;
        }
        this.inputElement.classList.remove(className);
    };
    ChipFieldAdapter.prototype.addInputEventListener = function (type, callback) {
        if (!this.inputElement) {
            return;
        }
        this.inputElement.addEventListener(type, callback);
    };
    ChipFieldAdapter.prototype.removeInputEventListener = function (type, callback) {
        if (!this.inputElement) {
            return;
        }
        this.inputElement.removeEventListener(type, callback);
    };
    ChipFieldAdapter.prototype.hasPlaceholder = function () {
        return this.inputElement && this.inputElement.placeholder ? this.inputElement.placeholder.trim().length > 0 : false;
    };
    ChipFieldAdapter.prototype.isDisabled = function () {
        if (!this.inputElement) {
            return false;
        }
        return this.inputElement.hasAttribute('disabled');
    };
    ChipFieldAdapter.prototype.hasValue = function () {
        var aMemberExists = !!this._component.querySelector(CHIP_FIELD_CONSTANTS.selectors.MEMBER);
        return aMemberExists;
    };
    ChipFieldAdapter.prototype.focusInput = function () {
        if (!this.inputElement) {
            return;
        }
        this.inputElement.focus();
    };
    ChipFieldAdapter.prototype.inputHasFocus = function () {
        return this.inputElement ? getActiveElement() === this.inputElement : false;
    };
    ChipFieldAdapter.prototype.inputHasContent = function () {
        return this.inputElement ? this.inputElement.value !== '' : false;
    };
    ChipFieldAdapter.prototype.initializeNotchedOutline = function () {
        return new MDCNotchedOutline(this._notchedOutlineElement);
    };
    ChipFieldAdapter.prototype.getLabelFontMetrics = function () {
        if (!this.labelElement) {
            throw new Error('labelElement must exist');
        }
        var style = getComputedStyle(this.labelElement);
        return {
            fontSize: parseInt(style.fontSize || '16', 10),
            fontFamily: style.fontFamily || 'Roboto'
        };
    };
    ChipFieldAdapter.prototype.setLabelAsRequiredForSafari = function (required) {
        // Due to a Safari bug with ::slotted::after selectors, we need to manually append the required "asterisk" to the label text
        // https://bugs.webkit.org/show_bug.cgi?id=178237
        if (required && Platform.WEBKIT && this.labelElement && !this.labelElement.innerText.endsWith('*')) {
            var asterisk = document.createElement('span');
            asterisk.style.color = 'var(--mdc-theme-error)';
            asterisk.style.marginLeft = '1px';
            asterisk.textContent = '*';
            this.labelElement.appendChild(asterisk);
        }
    };
    ChipFieldAdapter.prototype.getLabelWidth = function (fontSize, fontFamily) {
        if (!this.labelElement) {
            throw new Error('labelElement must exist');
        }
        return calculateFontWidth(this.labelElement.innerText, { fontSize: fontSize, fontFamily: fontFamily });
    };
    ChipFieldAdapter.prototype.hasLabel = function () {
        return !!this.labelElement;
    };
    ChipFieldAdapter.prototype.initializeFloatingLabel = function () {
        if (!this.labelElement) {
            throw new Error('labelElement must exist');
        }
        return new FloatingLabel(this.labelElement);
    };
    ChipFieldAdapter.prototype.ensureSlottedLabel = function () {
        if (!this.labelElement) {
            return;
        }
        this.labelElement.slot = 'label';
    };
    ChipFieldAdapter.prototype.getSlottedMemberElements = function () {
        return this._component.querySelectorAll(CHIP_FIELD_CONSTANTS.selectors.MEMBER);
    };
    ChipFieldAdapter.prototype.setInputAttributeObserver = function (listener) {
        var _this = this;
        if (!this.inputElement) {
            throw new Error('inputElement must exist');
        }
        this._inputMutationObserver = new MutationObserver(function (mutations) {
            for (var _i = 0, mutations_1 = mutations; _i < mutations_1.length; _i++) {
                var mutation = mutations_1[_i];
                if (mutation.attributeName && _this.inputElement) {
                    listener(mutation.attributeName, _this.inputElement.getAttribute(mutation.attributeName));
                }
            }
        });
        this._inputMutationObserver.observe(this.inputElement, { attributes: true, attributeFilter: ['disabled'] });
    };
    Object.defineProperty(ChipFieldAdapter.prototype, "inputElement", {
        get: function () {
            if (!this._inputElement) {
                this._inputElement = this._component.querySelector('input');
            }
            return this._inputElement;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ChipFieldAdapter.prototype, "labelElement", {
        get: function () {
            if (!this._labelElement) {
                this._labelElement = this._component.querySelector('label');
            }
            return this._labelElement;
        },
        enumerable: true,
        configurable: true
    });
    ChipFieldAdapter.prototype.ensureLabelOrder = function () {
        if (!this.labelElement || !this.inputElement) {
            return;
        }
        var children = Array.from(this._component.children);
        var childrenExist = children.length > 1;
        if (!childrenExist) {
            return;
        }
        var labelElementIndex = children.indexOf(this.labelElement);
        var inputElementIndex = children.indexOf(this.inputElement);
        if (labelElementIndex < inputElementIndex) {
            this._component.appendChild(this.labelElement);
        }
    };
    return ChipFieldAdapter;
}(BaseAdapter));
export { ChipFieldAdapter };
